import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Category, Transaction } from "@/contexts/ExpenseContext";

interface EditTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction | null;
  categories: Category[];
  onSave: (id: string, updates: Partial<Transaction>) => Promise<void>;
}

export default function EditTransactionDialog({
  open,
  onOpenChange,
  transaction,
  categories,
  onSave,
}: EditTransactionDialogProps) {
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    description: "",
    businessName: "",
    amount: "",
    categoryId: "",
    date: "",
    paymentType: "cash",
    cardNumber: "",
  });

  useEffect(() => {
    if (!transaction) return;
    setFormData({
      description: transaction.description.replace(/\s*\[(\d+)\/(\d+)\]\s*$/, "").trim(),
      businessName: transaction.businessName || "",
      amount: Math.abs(transaction.amount).toString(),
      categoryId: transaction.categoryId,
      date: transaction.date.split("T")[0],
      paymentType: transaction.paymentType || "cash",
      cardNumber: transaction.cardNumber || "",
    });
  }, [transaction]);

  const selectedCategory = categories.find((cat) => cat.id === formData.categoryId);
  const isIncomeCategory = selectedCategory?.type === "income";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transaction) return;

    const amount = parseFloat(formData.amount);
    if (!formData.description.trim() || !formData.categoryId || !Number.isFinite(amount) || amount <= 0) {
      return;
    }

    const category = categories.find((cat) => cat.id === formData.categoryId);
    if (!category) return;

    setSaving(true);
    try {
      await onSave(transaction.id, {
        description: formData.description.trim(),
        businessName: formData.businessName.trim() || undefined,
        amount: isIncomeCategory ? Math.abs(amount) : -Math.abs(amount),
        categoryId: formData.categoryId,
        category: category.name,
        date: formData.date,
        paymentType: formData.paymentType,
        cardNumber: formData.cardNumber || undefined,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir={i18n.language === "he" ? "rtl" : "ltr"}
        className="max-w-md border-[var(--border-color)] bg-[var(--bg-secondary)]"
      >
        <DialogHeader>
          <DialogTitle className="text-[16px] font-semibold text-[var(--text-primary)]">
            {t("transactions.editTransaction")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-description" className="text-[12px] text-[var(--text-secondary)]">
              {t("transactions.description")}
            </Label>
            <Input
              id="edit-description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder={t("transactions.descriptionPlaceholder")}
              className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-business-name" className="text-[12px] text-[var(--text-secondary)]">
              {t("transactions.businessName")}
            </Label>
            <Input
              id="edit-business-name"
              value={formData.businessName}
              onChange={(e) => setFormData((prev) => ({ ...prev, businessName: e.target.value }))}
              placeholder={t("transactions.businessNamePlaceholder")}
              className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-amount" className="text-[12px] text-[var(--text-secondary)]">
              {t("transactions.amount")}
            </Label>
            <Input
              id="edit-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={formData.amount}
              onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
              placeholder={t("transactions.amountPlaceholder")}
              className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] text-[var(--text-secondary)]">{t("transactions.category")}</Label>
            <Select
              value={formData.categoryId}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, categoryId: value }))}
            >
              <SelectTrigger className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]">
                <SelectValue placeholder={t("transactions.selectCategory")} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: category.color }} />
                      {category.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-date" className="text-[12px] text-[var(--text-secondary)]">
              {t("transactions.date")}
            </Label>
            <Input
              id="edit-date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
              className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[12px] text-[var(--text-secondary)]">{t("transactions.paymentType")}</Label>
            <Select
              value={formData.paymentType}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, paymentType: value }))}
            >
              <SelectTrigger className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]">
                <SelectValue placeholder={t("transactions.selectPaymentType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">{t("transactions.cash")}</SelectItem>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="debit_card">Debit Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="digital_wallet">Digital Wallet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.paymentType !== "cash" ? (
            <div className="space-y-2">
              <Label htmlFor="edit-card" className="text-[12px] text-[var(--text-secondary)]">
                Card Number (Last 4 digits)
              </Label>
              <Input
                id="edit-card"
                value={formData.cardNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, cardNumber: e.target.value }))}
                placeholder="****1234"
                maxLength={8}
                className="border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] text-[var(--text-primary)]"
              />
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="h-9 rounded-[8px] border-[var(--border-color)] bg-transparent text-[13px] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              {t("transactions.cancel")}
            </Button>
            <Button
              type="submit"
              className="h-9 rounded-[8px] bg-[var(--fg-accent)] text-[13px] text-white hover:bg-[var(--fg-accent-hover)]"
              disabled={saving}
            >
              {saving ? t("transactions.applying") : t("transactions.saveChanges")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
